import { filter, mergeMap, of } from 'rxjs'
import { catchError, retryWhen, withLatestFrom } from 'rxjs/operators'
import { serverIsNotAvailable } from '../../../../Framework/Epic/ServerNotAvailable.js'
import type { Epic } from '../../../index.js'
import { orderSlice } from '../../../Slice/Order/OrderSlice.js'
import { withBlockingRequest } from '../../Application/withBlockingRequest.js'
import { applicationSlice } from '../../../Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../../Model/Message/IMessage.js'
import type { ValidationIssue } from '../../../../Model/Order/ValidationIssue.js'
import { isNoChangedSamples } from '../../../../Model/Order/ValidationIssue.js'
import { isMisalignedFileReferences } from '../../../../Model/Order/ValidationIssue.js'
import {
  isInvalidProcessingTypeTaxonomyCombination,
  isInvalidMergedSample,
  isInvalidMultiSample,
  isInvalidTrimmedSample,
  isSampleNotMerged,
  isSampleNotTrimmed,
  isMissingReverseFile,
  isMixedSequencingPlatforms,
  isUnsupportedSampleConfigurationForPlatform,
  isUnsupportedProcessingTypeForPlatform,
  isPairedSamplesOnSingleEndPlatform
} from '../../../../Model/Order/ValidationIssue.js'
import {
  isAdditionalFile,
  isEmptyPool,
  isMissingFile,
  isNoItems,
  isSampleInvalid,
  isStorageBackendError
} from '../../../../Model/Order/ValidationIssue.js'
import type { AjaxError } from 'rxjs/ajax'
import { openModal } from '../../../Slice/ModalSlice/ModalSlice.js'

/** Missing-file lists can be long; show enough to act on and count the rest. */
const MAX_NAMED_MISSING = 20

const formatMissingNames = (names: string[]): string =>
  names.length > MAX_NAMED_MISSING
    ? `${names.slice(0, MAX_NAMED_MISSING).join(', ')} …and ${names.length - MAX_NAMED_MISSING} more`
    : names.join(', ')

export const SubmitOrderEpic: Epic = (
  action$,
  state$,
  { OrderRepository, [orderSlice.name]: order, [applicationSlice.name]: application }
) =>
  action$.pipe(
    filter(order.submitOrderRequest.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) =>
      withBlockingRequest(
        OrderRepository.submitOrder(action.payload, state.authentication.user).pipe(
          mergeMap((response) => {
            return of(order.submitOrderSuccess(response), openModal({ modalType: 'submitInfo', props: {} }))
          }),
          retryWhen(serverIsNotAvailable()),
          catchError((error: AjaxError) => {
            const data = error.response
            if (Array.isArray(data)) {
              const issues = data as ValidationIssue[]

              const messages: string[] = []

              const missingReverse = issues.filter(isMissingReverseFile)
              const missingReverseIds = new Set(missingReverse.map((issue) => issue.item.id))
              /* the same gap, reported by the field check too; the named R2 line below already covers it */
              const sampleInvalids = issues
                .filter(isSampleInvalid)
                .filter((issue) => !(missingReverseIds.has(issue.item.id) && issue.issue.includes('reverseFileId')))
              if (sampleInvalids.length > 0) {
                sampleInvalids.slice(0, 5).forEach((issue) => {
                  messages.push(`⚠️ Sample ${issue.item.customerSampleName} encountered validation issues: ${issue.issue}`)
                })

                let word = 'samples have'
                if (sampleInvalids.length == 1) {
                  word = 'sample has'
                }
                messages.push(`⚠️ ${sampleInvalids.length} ${word} invalid metadata (e.g., name, primer, etc.)`)
              }

              const missingFiles = issues.filter(isMissingFile)
              if (missingFiles.length > 0) {
                const fileword = missingFiles.length == 1 ? 'file' : 'files'
                // Naming them is the whole point: without the names the only possible reaction is
                // "re-upload all 1300 and hope", and the user cannot tell whether the retry worked.
                const names = missingFiles.map((issue) => issue.originalFileName).filter((name): name is string => !!name)

                messages.push(
                  `📂 ${missingFiles.length} ${fileword} did not arrive in our storage:` +
                    (names.length > 0 ? `\n   ${formatMissingNames(names)}` : '') +
                    '\n   Select these files again in their pool (or simply select all of them again) — ' +
                    'files that are already uploaded are skipped automatically.'
                )
              }

              const additionalFiles = issues.filter(isAdditionalFile)
              if (additionalFiles.length > 0) {
                let fileword = 'files were'
                if (additionalFiles.length == 1) {
                  fileword = 'file was'
                }
                messages.push(`📁 ${additionalFiles.length} unexpected ${fileword} uploaded. Contact our support for further steps.`)
              }

              if (issues.some(isNoItems)) {
                messages.push('🧪 No samples were found in the submission.')
              }

              if (issues.some(isEmptyPool)) {
                messages.push('🧬 Submission contains an empty pool of samples.')
              }

              if (issues.some(isStorageBackendError)) {
                messages.push('💾 A backend storage error occurred during submission. Please retry again later')
              }

              if (issues.some(isInvalidMultiSample)) {
                messages.push('Some fastq files where configured to contain multiple samples, but the order mode does not support that')
              }

              if (issues.some(isInvalidMergedSample)) {
                messages.push('Some samples where set to be already merged, but the order mode does not support that')
              }

              if (issues.some(isInvalidTrimmedSample)) {
                messages.push('Some samples where set to be already trimmed, but the order mode does not support that')
              }

              if (issues.some(isSampleNotMerged)) {
                messages.push('The order mode expects merged samples, but some samples are not marked as merged')
              }

              if (issues.some(isSampleNotTrimmed)) {
                messages.push('The order mode expects trimmed samples, but some samples are not marked as trimmed')
              }

              if (missingReverse.length > 0) {
                const names = missingReverse
                  .map((issue) => issue.item.customerSampleName || issue.item.forwardFile?.fileName)
                  .filter((name): name is string => !!name)
                messages.push(
                  `🧬 ${missingReverse.length} ${missingReverse.length === 1 ? 'sample is' : 'samples are'} missing the R2 file:` +
                    (names.length > 0 ? `\n   ${formatMissingNames(names)}` : '') +
                    '\n   Add the R2 files in the pool: drop them, use "Add R2" in the sample\'s row, or "Pair R2 files by name".'
                )
              }

              if (issues.some(isMisalignedFileReferences)) {
                messages.push('Some samples only partially refer to the same file, that is not allowed')
              }

              if (issues.some(isNoChangedSamples)) {
                messages.push(
                  'The order seems to have already been processed once, but no changes where detected. ' +
                    'Please contact support if this is intentional, currently, no samples would be send to processing.'
                )
              }

              const invalidProcessingTypeCombinations = issues.filter(isInvalidProcessingTypeTaxonomyCombination)
              if (invalidProcessingTypeCombinations.length > 0) {
                invalidProcessingTypeCombinations.forEach((issue) => messages.push(issue.issue))
              }

              issues.filter(isMixedSequencingPlatforms).forEach((issue) => messages.push(issue.issue))
              issues.filter(isUnsupportedSampleConfigurationForPlatform).forEach((issue) => messages.push(issue.issue))
              issues.filter(isUnsupportedProcessingTypeForPlatform).forEach((issue) => messages.push(issue.issue))
              issues.filter(isPairedSamplesOnSingleEndPlatform).forEach((issue) => messages.push(issue.issue))

              // Show a generic fallback if no known issues were extracted
              if (messages.length === 0) {
                messages.push('❌ Order submission failed due to unknown validation issues.')
              }

              return of(application.addMessage(errorMessage(messages.join('\n'))))
            } else {
              // Prefer backend-provided message if available
              let msg: string | null = null
              if (data && typeof data === 'object') {
                const candidate = (data as { message?: unknown }).message
                if (typeof candidate === 'string') {
                  msg = candidate
                }
              } else if (typeof data === 'string') {
                msg = data
              }

              return of(application.addMessage(errorMessage(msg ?? 'Order submission failed. Please try again or contact support.')))
            }
          })
        ),
        'Submitting Order',
        'Please wait, this operation may take longer depending on your order size'
      )
    )
  )
